import { z } from 'zod';

import { BEST_TOURNAMENTS_COUNT } from '@/modules/rankings/ppr/constants';
import { PlayerFiltersInterface } from '@/types/ranking.types';
import { getWeeksInLastYear } from '@/utils/helpers/globalRankingsUtils';

type ParamsSource = URLSearchParams | { get: (key: string) => string | null } | null | undefined;

export const partnerRankingsQuerySchema = z.object({
  type: z.string(),
  gender: z.string().optional(),
  live: z.string().optional(),
  country: z.string().optional(),
  state: z.string().optional(),
  rankBy: z.string().optional(),
  rankingType: z.string().optional(),
  page: z.string().optional(),
  week: z.string().optional(),
});

// Shared param-building for the `/data/partner_rankings` endpoint, used by both
// getPlayersRankings (JSON) and csvExportPlayersRankings (download_csv_flag CSV).
// Keep this the single source of truth so the two callers never drift apart.
export const buildPartnerRankingsParams = (rawParams: string): URLSearchParams => {
  const p = new URLSearchParams(rawParams);
  const searchParams = new URLSearchParams();

  const firstWeek = getWeeksInLastYear();
  const defaults = getDefaultFilters(p);
  const validatedParams = partnerRankingsQuerySchema.parse({
    type: defaults.type,
    gender: p.get('gender') ?? undefined,
    live: defaults.live,
    country: defaults.country,
    state: defaults.state,
    rankBy: defaults.rankBy,
    rankingType: defaults.rankingType,
    page: p.get('page') ?? undefined,
    week: p.get('week') ?? undefined,
  });

  const isProWorldRanking = validatedParams.rankingType === 'proWorldRanking';

  searchParams.set('partner', 'ppa');
  searchParams.set('division_type', validatedParams.type);
  // The "Overall" tab intentionally passes gender='' so backend returns both genders.
  if (validatedParams.gender === undefined) {
    searchParams.set('gender', 'F');
  } else if (validatedParams.gender !== '') {
    searchParams.set('gender', validatedParams.gender);
  }
  const isCurrentSeed = validatedParams.rankingType === 'currentSeed';
  const useRank = isProWorldRanking || isCurrentSeed || validatedParams.rankBy === 'rank';
  searchParams.set('page_size', '100');
  searchParams.set('current_page', validatedParams.page ?? '1');
  searchParams.set('topN', BEST_TOURNAMENTS_COUNT.toString());
  searchParams.set('is_live', validatedParams.live ?? '');

  if (useRank && firstWeek && firstWeek[0]) {
    searchParams.set('rank', validatedParams.week || firstWeek[0].id);
  }

  if (validatedParams.live === '' || validatedParams.live === undefined) {
    searchParams.set('country_id', validatedParams.country ?? '');
    searchParams.set('state_id', validatedParams.state ?? '');
  }

  return searchParams;
};

export const getDefaultFilters = (params: ParamsSource): PlayerFiltersInterface => {
  const rankingType = params?.get('ranking_type') ?? 'proWorldRanking';
  const defaultType = rankingType === 'proWorldRanking' ? '8' : '1';
  const firstWeek = getWeeksInLastYear();

  return {
    type: params?.get('type') ?? defaultType,
    rankBy: params?.get('rank_by') ?? 'race',
    rankingType,
    country: params?.get('country') ?? '',
    state: params?.get('state') ?? '',
    gender: params?.get('gender') ?? 'F',
    live: params?.get('live') ?? '',
    week: params?.get('week') ?? firstWeek[0]?.id,
  };
};

// Points are displayed as whole numbers, rounded (no decimals) per PW-7330.
export const formatPoints = (points: number): string => {
  if (!Number.isFinite(points)) {
    return `${points}`;
  }
  return `${Math.round(points)}`;
};

export const getNewSearchParams = (filters: PlayerFiltersInterface) => {
  const newSearchParams = new URLSearchParams();

  if (filters.rankBy) {
    newSearchParams.set('rank_by', filters.rankBy);
  }
  if (filters.rankingType) {
    newSearchParams.set('ranking_type', filters.rankingType);
  }
  if (filters.country) {
    newSearchParams.set('country', filters.country);
  }
  if (filters.state) {
    newSearchParams.set('state', filters.state);
  }
  if (filters.type) {
    newSearchParams.set('type', filters.type);
  }
  if (filters.gender) {
    newSearchParams.set('gender', filters.gender);
  }
  if (filters.live) {
    newSearchParams.set('live', filters.live);
  }
  if ((filters.rankingType || filters.rankBy === 'rank' || filters.rankBy === '') && filters.week) {
    newSearchParams.set('week', filters.week);
  }

  return newSearchParams || '';
};
